import * as React from "react";
import { useLayoutEffect, useRef, useState } from "react";
import { motion, animate, AnimatePresence } from "framer-motion";
import * as style from "./style.module.css";
import logo from "/src/images/icon/logo.png";

const STORAGE_KEY = "kk_loading_seen_v6";

const PROGRESS_MS = 2000; // 0%→100%までカウントアップする時間
const LOGO_HOLD_MS = 1600; // 100%からロゴへ切り替わり、保持する時間
const FADE_MS = 1200; // 画面全体がフェードアウトする時間

/**
 * サイト初回訪問時にのみ再生するローディング演出。
 * 0%→100%のカウントアップ → ロゴへ切り替え → 画面全体フェードアウト、の3段階。
 *
 * 実際のアセット読み込み進捗を追跡しているわけではなく、
 * 見た目としてのカウントアップ（演出）である点に注意。
 */
const LoadingScreen = ({ children }) => {
  const [stage, setStage] = useState("progress"); // "progress" -> "logo" -> "exit" -> "done"
  const [percent, setPercent] = useState(0);
  const timers = useRef([]);

  useLayoutEffect(() => {
    let alreadySeen = false;
    try {
      alreadySeen = sessionStorage.getItem(STORAGE_KEY) === "1";
    } catch (e) {}

    const prefersReduced = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    if (alreadySeen || prefersReduced) {
      setStage("done");
      return;
    }

    try {
      sessionStorage.setItem(STORAGE_KEY, "1");
    } catch (e) {}

    const controls = animate(0, 100, {
      duration: PROGRESS_MS / 1000,
      ease: "easeInOut",
      onUpdate: (latest) => setPercent(Math.round(latest)),
      onComplete: () => setStage("logo"),
    });

    return () => controls.stop();
  }, []);

  React.useEffect(() => {
    if (stage === "logo") {
      timers.current.push(setTimeout(() => setStage("exit"), LOGO_HOLD_MS));
    }
    if (stage === "exit") {
      timers.current.push(setTimeout(() => setStage("done"), FADE_MS));
    }
    return () => timers.current.forEach(clearTimeout);
  }, [stage]);

  React.useEffect(() => {
    if (typeof document === "undefined") return;
    document.body.style.overflow = stage === "done" ? "" : "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [stage]);

  return (
    <>
      {children}

      {stage !== "done" && (
        <motion.div
          className={style.wrap}
          aria-hidden="true"
          animate={{ opacity: stage === "exit" ? 0 : 1 }}
          transition={{ duration: FADE_MS / 1000, ease: "easeInOut" }}
        >
          <AnimatePresence mode="wait">
            {stage === "progress" ? (
              <motion.span
                key="percent"
                className={style.percent}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.35, ease: "easeIn" }}
              >
                {percent}
                <span className={style.percentSign}>%</span>
              </motion.span>
            ) : (
              <motion.img
                key="logo"
                src={logo}
                alt="金田建材株式会社"
                className={style.logo}
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
              />
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </>
  );
};

export default LoadingScreen;